import type { CallOutcome, Household, Owner } from '../api/types';

// Ergebnis je weiterem Objekt des Eigentümers bzw. Haushalts im selben Gespräch (OFFENE_FRAGEN B-17, B-20).
export type ObjectChoice = '' | 'NOT_INTERESTED' | 'FU_3' | 'FU_6' | 'FU_12' | 'OTHER' | 'APPOINTMENT' | 'APPOINTMENT_DONE';

export const objectChoiceLabels: Record<Exclude<ObjectChoice, 'APPOINTMENT_DONE'>, string> = {
  '': 'Nicht besprochen',
  NOT_INTERESTED: 'Kein Interesse',
  FU_3: 'Wiedervorlage in 3 Monaten',
  FU_6: 'Wiedervorlage in 6 Monaten',
  FU_12: 'Wiedervorlage in 12 Monaten',
  OTHER: 'Besprochen, noch offen',
  APPOINTMENT: 'Termin eintragen …',
};

export type OwnerObject = Owner['properties'][number] & { ownerName: string; household?: boolean };

// otherObjects: die weiteren aktiven Leads aller Eigentümer eines Objekts und der weiteren Personen im Haushalt (jeder
// einmal, Eigentümer zuerst).
export function otherObjects(owners: Owner[], leadId: string, household?: Household): OwnerObject[] {
  const seen = new Set<string>([leadId]);
  const out: OwnerObject[] = [];
  const add = (p: Owner['properties'][number], ownerName: string, inHousehold: boolean) => {
    if (!seen.has(p.id)) {
      seen.add(p.id);
      out.push({ ...p, ownerName, household: inHousehold });
    }
  };
  for (const o of owners) for (const p of o.properties) add(p, o.name, false);
  for (const m of household?.members ?? []) for (const p of m.properties) add(p, m.name, true);
  return out;
}

// monthsAhead: 10:00 Uhr in n Monaten, auf einen Werktag (Mo–Fr) gelegt.
export function monthsAhead(n: number, now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth() + n, now.getDate(), 10, 0, 0);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

// objectsPayload: gewählte Ergebnisse für die API (Termine laufen über das Terminformular, „Nicht besprochen“ entfällt).
export function objectsPayload(choices: Record<string, ObjectChoice>, now = new Date()): NonNullable<CallOutcome['objects']> {
  const out: NonNullable<CallOutcome['objects']> = [];
  for (const [leadId, c] of Object.entries(choices)) {
    if (c === 'NOT_INTERESTED' || c === 'OTHER') out.push({ lead_id: leadId, outcome: c });
    else if (c === 'FU_3' || c === 'FU_6' || c === 'FU_12') {
      out.push({ lead_id: leadId, outcome: 'FOLLOW_UP', follow_up: { due_at: monthsAhead(Number(c.slice(3)), now) } });
    }
  }
  return out;
}

// reachedOutcomes: Nur wenn der Eigentümer erreicht wurde, gelten Ergebnisse für weitere Objekte.
export const reachedOutcomes = ['APPOINTMENT', 'CALLBACK', 'NOT_INTERESTED', 'FOLLOW_UP', 'OTHER', 'CLARIFICATION'];

export const roleText: Record<string, string> = {
  OWNER: 'Eigentümer',
  CO_OWNER: 'Miteigentümer',
  HEIR: 'Erbe',
  PROXY: 'Bevollmächtigter',
  MANAGER: 'Verwalter',
  HOUSEHOLD: 'Haushalt',
};
